import { Icon, type IconTypes } from '@procurify/ui'
import { type IntlShape } from 'react-intl'

import { useProcurifyIntl } from '@webapp/hooks'
import expenseTypeLabelMessages from '@webapp/modules/expense/components/ExpenseTypeLabel/messages'
import {
  ExpenseTypes,
  type ExpenseTypeEnum,
} from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import { ExpenseTypeSelectorContainerStyled } from './styles/ExpenseTypeSelectorContainerStyled'
import { ExpenseTypeTileIconStyled } from './styles/ExpenseTypeTileIconStyled'
import { ExpenseTypeTileLabelStyled } from './styles/ExpenseTypeTileLabelStyled'
import { ExpenseTypeTilesRowStyled } from './styles/ExpenseTypeTilesRowStyled'
import { ExpenseTypeTileStyled } from './styles/ExpenseTypeTileStyled'

const assertNever = (value: never): never => {
  throw new Error(`Unhandled ExpenseTypeEnum value: ${String(value)}`)
}

const EXPENSE_TYPE_ICON_MAP: Record<ExpenseTypeEnum, IconTypes> = {
  [ExpenseTypes.OTHER]: 'other',
  [ExpenseTypes.MEALS]: 'meals',
  [ExpenseTypes.MILEAGE]: 'mileage',
  [ExpenseTypes.LABOUR]: 'labour',
  [ExpenseTypes.AIRFARE]: 'flight',
  [ExpenseTypes.LODGING]: 'lodge',
}

const EXPENSE_TYPE_ORDER: ExpenseTypeEnum[] = [
  ExpenseTypes.OTHER,
  ExpenseTypes.AIRFARE,
  ExpenseTypes.LABOUR,
  ExpenseTypes.LODGING,
  ExpenseTypes.MEALS,
  ExpenseTypes.MILEAGE,
]

const getLabel = (intl: IntlShape, type: ExpenseTypeEnum): string => {
  switch (type) {
    case ExpenseTypes.OTHER:
      return intl.formatMessage(expenseTypeLabelMessages.expenseTypeOther)
    case ExpenseTypes.MEALS:
      return intl.formatMessage(expenseTypeLabelMessages.expenseTypeMeals)
    case ExpenseTypes.MILEAGE:
      return intl.formatMessage(expenseTypeLabelMessages.expenseTypeMileage)
    case ExpenseTypes.LABOUR:
      return intl.formatMessage(expenseTypeLabelMessages.expenseTypeLabour)
    case ExpenseTypes.AIRFARE:
      return intl.formatMessage(expenseTypeLabelMessages.expenseTypeAirfare)
    case ExpenseTypes.LODGING:
      return intl.formatMessage(expenseTypeLabelMessages.expenseTypeLodging)
    default:
      return assertNever(type)
  }
}

interface ExpenseTypeSelectorProps {
  value: ExpenseTypeEnum
  onChange: (expenseType: ExpenseTypeEnum) => void
  disabled?: boolean
}

export const ExpenseTypeSelector = ({
  value,
  onChange,
  disabled = false,
}: ExpenseTypeSelectorProps) => {
  const intl = useProcurifyIntl()

  return (
    <ExpenseTypeSelectorContainerStyled>
      <ExpenseTypeTilesRowStyled>
        {EXPENSE_TYPE_ORDER.map((expenseType) => {
          const isActive = value === expenseType
          return (
            <ExpenseTypeTileStyled
              key={expenseType}
              type='button'
              isActive={isActive}
              isDisabled={disabled}
              disabled={disabled}
              onClick={() => onChange(expenseType)}
            >
              <ExpenseTypeTileIconStyled isActive={isActive}>
                <Icon
                  icon={EXPENSE_TYPE_ICON_MAP[expenseType]}
                  size={24}
                  color={isActive ? 'white' : 'primary'}
                />
              </ExpenseTypeTileIconStyled>
              <ExpenseTypeTileLabelStyled isActive={isActive}>
                {getLabel(intl, expenseType)}
              </ExpenseTypeTileLabelStyled>
            </ExpenseTypeTileStyled>
          )
        })}
      </ExpenseTypeTilesRowStyled>
    </ExpenseTypeSelectorContainerStyled>
  )
}
